import { signal, effect } from "alien-signals";
import { html, render } from "uhtml";
import {
  calculatePageData,
  pageData,
  pdfSize,
  scale,
} from "../view-models/page-signals.js";
import { TextLayer, pdfDocument } from "../view-models/book-signals.js";
import {
  viewportHeight,
  viewportWidth,
} from "../view-models/platform/viewport-signals.js";
import { locale } from "../view-models/app-signals.js";
import { locales } from "../models/locales.js";

export const pageElements = {
  start: signal(),
  end: signal(),
  root: signal(),
};

effect(() => {
  const root = pageElements.root();
  const data = pageData();
  if (root) {
    root.data = data;
  }
});

// Every render request that has not settled yet, so that callers (tests,
// and anything that needs a painted page) can wait for the reader to be
// idle. Each page element gets a chain of renders: PDF.js refuses two
// concurrent render() calls on one canvas, and a request that has been
// superseded by a newer one for the same element is skipped rather than
// painted, so rapid page turns do not queue up stale work.
const pendingRenders = new Set();
const elementRenders = new WeakMap();

/** Resolves once no page render is queued or in progress. */
export async function renderComplete() {
  while (pendingRenders.size > 0) {
    await Promise.allSettled([...pendingRenders]);
  }
}

export function renderPage(element, pageNumber, calculateViewport) {
  if (!element) return Promise.resolve();
  let state = elementRenders.get(element);
  if (!state) {
    state = { generation: 0, chain: Promise.resolve() };
    elementRenders.set(element, state);
  }
  const generation = ++state.generation;
  const task = state.chain.then(() => {
    if (generation !== state.generation) return;
    return drawPage(element, pageNumber, calculateViewport);
  });
  state.chain = task.catch(() => {});
  pendingRenders.add(task);
  const settle = () => pendingRenders.delete(task);
  task.then(settle, settle);
  return task;
}

// Text layers still streaming, with the document each belongs to. PDF.js
// never ends a text stream whose document was destroyed, so a layer left
// waiting would hang its page's render chain and stay in PDF.js's set of
// pending layers. Cancel them as soon as the document is replaced.
const streamingTextLayers = new Map();

effect(() => {
  const current = pdfDocument();
  for (const [textLayer, pdf] of streamingTextLayers) {
    if (pdf !== current) textLayer.cancel();
  }
});

function reload() {
  window.location.reload();
}

// The message shown over a page that failed to paint. It names the error
// itself, so that a screenshot from a device without developer tools
// (an old iPad, say) says what went wrong. It sits beside the canvas and
// text layer rather than in the element's template, so a later paint of
// the page can remove it without re-rendering the element.
function showPageError(element, err) {
  let message = element.querySelector(".cassis-pdf-page-error");
  if (!message) {
    message = document.createElement("div");
    message.className = "cassis-pdf-page-error";
    message.setAttribute("role", "alert");
    element.append(message);
  }
  render(message, errorContent(locale(), err));
}

// What browsers say when code uses a method or global they lack, as
// PDF.js does on older ones: Chrome and Firefox ("x.toHex is not a
// function", "Iterator is not defined") and Safari ("Can't find
// variable: Iterator"). It is the message, not the error's name, that
// tells: PDF.js passes on nearly every error from its worker, a corrupt
// stream included, as UnknownErrorException, and being offline is a
// TypeError too ("Failed to fetch").
const MISSING_FEATURE =
  /\bis not (a function|defined|a constructor|iterable)\b|Can't find variable/;

/** Whether `err` most likely comes from an out-of-date browser. */
export function likelyOutdatedBrowser(err) {
  return typeof err?.message === "string" && MISSING_FEATURE.test(err.message);
}

const localised = (strings, lang) => strings[lang] || strings.en;

/**
 * What went wrong, in words a reader can act on, then the error itself
 * and a reload button: the content of both the page error and the
 * document's load error. When the browser is probably out of date the
 * reader is told so first, as that is what they can fix.
 */
export function errorContent(lang, err) {
  const summary = likelyOutdatedBrowser(err)
    ? html`<p><strong>${localised(locales.outdatedBrowser, lang)}</strong></p>
        <p>${localised(locales.outdatedBrowserAdvice, lang)}</p>`
    : html`<p>${localised(locales.errorLabel, lang)}</p>`;
  return html`${summary}
    <p class="cassis-pdf-error-detail">${errorDetail(err)}</p>
    <button type="button" @click=${reload}>
      ${localised(locales.tryAgain, lang)}
    </button>`;
}

/** A one-line description of `err` for the reader to report. */
export function errorDetail(err) {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}

function clearPageError(element) {
  element.querySelector(".cassis-pdf-page-error")?.remove();
}

function clearPage(element) {
  const canvas = element.querySelector(".cassis-pdf-canvas-layer");
  const text = element.querySelector(".cassis-pdf-text-layer");
  canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  text?.replaceChildren();
  clearPageError(element);
  element.rendered = false;
  element.renderedPage = null;
}

async function drawPage(element, pageNumber, calculateViewport) {
  const pdf = pdfDocument();
  if (!element.ratio || !pdf || !Number.isInteger(pageNumber)) return;
  if (pageNumber < 1 || pageNumber > pdf.numPages) {
    // An end page past the last page of an odd-length document, or a stale
    // page number from a previous document: show nothing rather than ask
    // PDF.js for a page that does not exist.
    clearPage(element);
    return;
  }
  let ratio = element.ratio;
  let textLayer = null;
  element.rendered = false;
  try {
    const page = await pdf.getPage(pageNumber);
    if (calculateViewport) {
      const SCALE = 1.0;
      const viewport = page.getViewport({ scale: SCALE });
      const oldViewport = pdfSize();
      if (
        viewport.height !== oldViewport.height ||
        viewport.width !== oldViewport.width
      ) {
        pdfSize(viewport);
        const calculations = calculatePageData(
          viewport,
          viewportHeight(),
          viewportWidth(),
          scale(),
        );
        ratio = calculations.ratio;
      }
    }
    const canvas = element.querySelector(".cassis-pdf-canvas-layer");
    const text = element.querySelector(".cassis-pdf-text-layer");
    if (!canvas || !text) return;
    text.replaceChildren();
    const scaledViewport = page.getViewport({ scale: ratio * scale() });
    const renderTask = page.render({
      canvasContext: canvas.getContext("2d"),
      viewport: scaledViewport,
    });
    textLayer = new TextLayer({
      // Not getTextContent(): it async-iterates a ReadableStream, which
      // Safari 18 cannot do, and the legacy build does not
      // polyfill. TextLayer reads the stream with getReader() instead.
      textContentSource: page.streamTextContent(),
      viewport: scaledViewport,
      container: text,
    });
    streamingTextLayers.set(textLayer, pdf);
    // Replaced while the page was loading: the effect has already run.
    if (pdf !== pdfDocument()) textLayer.cancel();
    await Promise.all([renderTask.promise, textLayer.render()]);
    if (element.lastPage && element.lastPage !== page) {
      // Release the parsed resources of the page this element showed
      // before; PDF.js would otherwise keep every visited page in memory.
      element.lastPage.cleanup();
    }
    element.lastPage = page;
    element.rendered = true;
    element.renderedPage = pageNumber;
    element.removeAttribute("data-render-error");
    clearPageError(element);
  } catch (err) {
    // Stop reading text for a page that will not be shown.
    textLayer?.cancel();
    // A document replaced while this page was painting is not an error
    // the reader needs to see.
    if (pdf !== pdfDocument()) return;
    // One page failing to paint does not make the document unreadable,
    // but a blank page with no explanation looks like it is.
    element.renderedPage = null;
    element.setAttribute("data-render-error", "");
    console.error(err);
    showPageError(element, err);
  } finally {
    streamingTextLayers.delete(textLayer);
  }
}
